import React, { useState } from "react";
import { useSelector } from "react-redux";

import astronaut from "../../../_assets/img/registration/astronaut-sm.png";

const NEXT_STEPS = [
    "Öffne die E-Mail von pathway solutions.",
    "Klicke auf „Registrierung abschließen“.",
    "Vergib dein Passwort und leg los.",
];

// Gift icon for the Steuercheck bonus banner (matches the design's inline SVG).
const GiftIcon = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
        <rect x="3" y="8" width="18" height="4" rx="1" />
        <path d="M12 8v13M20 12v8a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1v-8M7.5 8a2.5 2.5 0 1 1 0-5C11 3 12 8 12 8s1-5 4.5-5a2.5 2.5 0 1 1 0 5" />
    </svg>
);

/**
 * Step 4 — success screen, two variants:
 *  4a (email): "Bestätige deine E-Mail" — confirm-email + next steps + resend/change-email.
 *  4b (sso):   "Accounterstellung erfolgreich" — auto-login CTA into the app.
 */
const SuccessStep = ({ method, email, onGoToAccount, onChangeEmail, onResend }) => {
    const isSso = method === "sso";
    // resend outcome comes from Redux — the thunk swallows failures (sets messages) rather than
    // rejecting, so only treat it as sent when success is actually reported.
    const resendSuccess = useSelector((state) => state.authentication.successMessage);
    const resendError = useSelector((state) => state.authentication.errorMessage);
    const [attempted, setAttempted] = useState(false);
    const resent = attempted && Boolean(resendSuccess);

    const handleResend = () => {
        if (resent) return;
        setAttempted(true);
        onResend?.(); // dispatch clears both messages, then sets success or error
    };

    return (
        <div className="registration-step registration-step--success" data-cy="registration-step-success">
            <img className="registration-success__illustration" src={astronaut} alt="" aria-hidden />

            {isSso ? (
                <>
                    <p className="registration-step__eyebrow">Willkommen an Bord!</p>
                    <h1 className="registration-step__title">Accounterstellung erfolgreich</h1>
                    <p className="registration-success__copy">
                        Dein Account ist eingerichtet. Leg direkt los und richte deine Verkaufskanäle ein.
                    </p>
                    <button type="button" className="registration-cta" onClick={onGoToAccount} data-cy="registration-success-to-account">
                        Jetzt zum Account →
                    </button>
                </>
            ) : (
                <>
                    <p className="registration-step__eyebrow">Fast geschafft!</p>
                    <h1 className="registration-step__title">Bestätige deine E-Mail</h1>
                    <p className="registration-success__copy">
                        Wir haben eine E-Mail an <strong>{email}</strong> geschickt.
                    </p>
                    <p className="registration-success__copy">
                        Klicke auf den Link in der E-Mail, um dein Passwort zu setzen und die Registrierung abzuschließen.
                    </p>

                    <div className="registration-nextsteps">
                        <p className="registration-nextsteps__title">Nächste Schritte</p>
                        {NEXT_STEPS.map((text, i) => (
                            <div key={text} className="registration-nextsteps__item">
                                <span className="registration-nextsteps__num">{i + 1}</span>
                                <span className="registration-nextsteps__text">{text}</span>
                            </div>
                        ))}
                    </div>
                </>
            )}

            {/* Steuercheck bonus banner — shown on both variants */}
            <div className="registration-success__bonus" data-cy="registration-bonus-banner">
                <span className="registration-success__bonus-icon">
                    <GiftIcon />
                </span>
                <p className="registration-success__bonus-text">
                    <strong>Bonus:</strong> Schließe dein Onboarding innerhalb von 7 Tagen ab und erhalte einen{" "}
                    <strong>kostenlosen Steuercheck</strong>.
                </p>
            </div>

            {!isSso && (
                <div className="registration-success__actions">
                    <p className="registration-success__resend">
                        Keine E-Mail erhalten?{" "}
                        <button
                            type="button"
                            className="registration-link"
                            onClick={handleResend}
                            disabled={resent}
                            data-cy="registration-resend-email"
                        >
                            {resent ? "E-Mail wurde erneut gesendet" : "Erneut senden"}
                        </button>
                    </p>
                    {attempted && !resendSuccess && resendError && (
                        <p className="registration-input__error" role="alert">
                            {resendError}
                        </p>
                    )}
                    <p className="registration-success__change">
                        Falsche Adresse?{" "}
                        <button type="button" className="registration-link registration-link--muted" onClick={onChangeEmail} data-cy="registration-change-email">
                            E-Mail ändern
                        </button>
                    </p>
                </div>
            )}
        </div>
    );
};

export default SuccessStep;
